import { handleIdGeneration } from '@common/utils'
import {
  MolsonAccordion,
  MolsonBox,
  MolsonButton,
  MolsonIcon,
  MolsonTypography
} from '@components/reusable'
import { type Dispatch, type SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FitItem, MIN_LENGTH } from './FitItem'

export type Option = {
  dnp?: number | string
  id?: number
  name?: null | string
  isNewValue?: boolean
}

export type SaveFn = (props: {
  isUpdating: boolean
  name?: string | null
  optionId?: number
  dnp?: number
}) => void

export type CustomFitProps = {
  expanded: boolean
  onDelete: (id: number) => void
  onSave?: SaveFn
  options: Option[]
  setExpanded: Dispatch<SetStateAction<boolean>>
}

enum FitActions {
  ADD = 'add',
  REMOVE = 'remove'
}

export const CustomFit = ({
  expanded,
  onDelete,
  onSave,
  options,
  setExpanded
}: CustomFitProps) => {
  const [customFitsList, setCustomFitsList] = useState(options)

  const { t } = useTranslation('', {
    keyPrefix: 'common'
  })

  const handleFitState = (
    action: FitActions = FitActions.ADD,
    fitId?: number
  ) => {
    if (action === FitActions.ADD) {
      setCustomFitsList(prevFits => [
        ...prevFits,
        {
          dnp: 0,
          id: Number(handleIdGeneration()),
          name: '',
          isNewValue: true
        }
      ])
    } else {
      setCustomFitsList(prevFits => prevFits.filter(fit => fit.id !== fitId))
    }
  }

  const onRemoveFit = (fitId?: number) => {
    if (fitId && Number(fitId) < 0) {
      onDelete(fitId)
    } else {
      handleFitState(FitActions.REMOVE, Number(fitId))
    }
  }

  const onSetFitList = (list: Option[]) => setCustomFitsList(list)

  const addNewFitDisabled = customFitsList.some(item =>
    item.name ? item.name?.length < MIN_LENGTH : true
  )

  return (
    <MolsonAccordion
      expanded={expanded}
      onChange={() => setExpanded(prev => !prev)}
      primaryText={t('custom')}
    >
      <MolsonTypography mb={1.25} mt={2}>
        {t('optionName')}
      </MolsonTypography>

      <MolsonBox display="flex" flexDirection="column" gap={1}>
        {customFitsList.map((fit, index) => (
          <FitItem
            customFitsList={customFitsList}
            fitItem={fit}
            index={index}
            key={fit.id}
            onRemoveFit={onRemoveFit}
            onSave={onSave}
            onSetFitList={onSetFitList}
          />
        ))}

        <MolsonButton
          color="secondary"
          disabled={addNewFitDisabled}
          onClick={() => handleFitState()}
          startIcon={<MolsonIcon variant="PLUS" fontSize="small" />}
        >
          {t('newOption')}
        </MolsonButton>
      </MolsonBox>
    </MolsonAccordion>
  )
}
